import {
  Stack,
  Title,
  Group,
  ThemeIcon,
  Badge,
  Accordion,
  TextInput,
  ActionIcon,
  Button,
} from "@mantine/core"
import { IconAlertCircle, IconArrowUp } from "@tabler/icons-react"
import { useMemo, useState } from "react"
import { useParams } from "react-router-dom"

import {
  DigestResponses,
  OutputDigestSchema,
} from "@costory/types/endpoints/digest"

import dayjs from "@costory/shared/dayjs"
import { formatNumber } from "@costory/shared/utils/format"

import { AccordionTile } from "@costory/front/components/accordionTile/AccordionTile"
import { QueryWrapper } from "@costory/front/components/layout/QueryWrapper"
import { DigestTiles } from "@costory/front/pages/DigestV2/components/DigestTiles"
import {
  useEmailDigestReportMutation,
  useSummaryDigestQuery,
} from "@costory/front/queries/digest"

const DigestSummaryPage = ({
  digest,
  limit,
}: {
  digest: DigestResponses.DigestExport
  limit: number
}) => {
  const [value, setValue] = useState<string[]>([])
  const [allExpanded, setAllExpanded] = useState(false)
  const [toEmail, setToEmail] = useState("")
  const error = useMemo(() => {
    if (!toEmail) return null
    const result = OutputDigestSchema.shape.toEmail.safeParse(toEmail)
    return result.success ? null : result.error.errors[0].message
  }, [toEmail])
  const { mutateAsync, isPending } = useEmailDigestReportMutation()

  const factorsWithContent = digest.factors
    .slice(0, digest.limit)
    .filter((factor) => factor.details.match(/\[!\[img\][^\n]*/)?.[0])

  const handleExpandCollapseAll = () => {
    if (allExpanded) {
      setValue([])
      setAllExpanded(false)
    } else {
      // Expand all items that have content
      const allKeys = factorsWithContent.map((factor) => factor.key.toString())
      setValue(allKeys)
      setAllExpanded(true)
    }
  }

  return (
    <Stack>
      <Title order={1}>{digest.name} </Title>
      <Title order={6} c="textMuted">
        {dayjs(digest.period).format("MMM YYYY")} vs{" "}
        {dayjs(digest.previousPeriod).format("MMM YYYY")}
      </Title>
      <DigestTiles
        tiles={digest.tiles}
        costColumn={digest.costColumn}
        period={digest.period}
        showPlaceholders={false}
        exclusionWhereClause={digest.exclusionWhereClause}
      />
      <Group mb={6} justify="space-between">
        <Group>
          <ThemeIcon radius="md" size="lg" color="teal">
            <IconAlertCircle size={24} />
          </ThemeIcon>

          <Group justify="space-between">
            <Title order={2}> Main Contributing Factors </Title>
            <Badge
              color="primary"
              data-intercom-target="costory-tour-digest-summary-percent-explained"
            >
              {digest.percentExplained > 1
                ? "`>100%`"
                : formatNumber({
                    value: digest.percentExplained,
                    numberType: "percent",
                    maxDigit: 0,
                  })}{" "}
              explained
            </Badge>
          </Group>

          {factorsWithContent.length > 0 && (
            <Button
              variant="subtle"
              size="xs"
              onClick={handleExpandCollapseAll}
              data-intercom-target="costory-tour-digest-summary-expand-all"
            >
              {allExpanded ? "Collapse All" : "Expand All"}
            </Button>
          )}
        </Group>
        <TextInput
          size="md"
          placeholder="Enter your email address"
          rightSectionWidth={42}
          leftSection="@"
          disabled={isPending}
          value={toEmail}
          error={error}
          errorProps={{ size: "xs" }}
          onChange={(e) => setToEmail(e.currentTarget.value)}
          data-intercom-target="costory-tour-digest-summary-email-input"
          rightSection={
            <ActionIcon
              size={32}
              variant="filled"
              onClick={() =>
                mutateAsync({
                  digestId: digest.digestId,
                  toEmail,
                  limit,
                })
              }
              loading={isPending}
              disabled={isPending || !!error}
              data-intercom-target="costory-tour-digest-summary-send-email"
            >
              <IconArrowUp size={18} stroke={1.5} />
            </ActionIcon>
          }
        />
      </Group>
      <Accordion
        variant="separated"
        multiple={true}
        value={value}
        onChange={(newValue) => {
          setValue(newValue)
          // Update allExpanded state based on whether all expandable items are open
          const allKeys = factorsWithContent.map((factor) =>
            factor.key.toString(),
          )
          setAllExpanded(
            allKeys.length > 0 &&
              allKeys.every((key) => newValue.includes(key)),
          )
        }}
      >
        {digest.factors.slice(0, digest.limit).map((factor) => (
          <AccordionTile
            factor={factor}
            key={factor.key}
            isExpanded={value.includes(factor.key.toString())}
          />
        ))}
      </Accordion>
    </Stack>
  )
}

export const SummaryPageWrapper = () => {
  const { digestId, limit } = useParams<{ digestId: string; limit: string }>()
  const parsedLimit = limit && limit != "" ? Number(limit) : 10
  const digestsQuery = useSummaryDigestQuery(digestId!, parsedLimit)
  return (
    <QueryWrapper query={digestsQuery} allowEmptyArray>
      {({ data }) => <DigestSummaryPage digest={data} limit={parsedLimit} />}
    </QueryWrapper>
  )
}
